import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const radioGroupVariants = cva("setu:grid setu:gap-2", {
    variants: {
        orientation: {
            horizontal: "setu:flex setu:flex-row setu:gap-2",
            vertical: "setu:grid setu:gap-2",
        },
    },
    defaultVariants: {
        orientation: "vertical",
    },
});

const radioGroupItemVariants = cva(
    "setu:!border-input setu:!text-primary setu:focus-visible:!border-ring setu:focus-visible:ring-ring/50 setu:aria-invalid:ring-destructive/20 setu:dark:aria-invalid:ring-destructive/40 setu:aria-invalid:!border-destructive setu:dark:bg-input/30 setu:aspect-square setu:size-4 setu:shrink-0 setu:rounded-full setu:!border setu:shadow-xs setu:transition-[color,box-shadow] setu:outline-none setu:focus-visible:ring-[3px] setu:disabled:cursor-not-allowed setu:disabled:opacity-50 setu:data-[state=checked]:!border-primary",
    {
        variants: {
            size: {
                default: "setu:size-4",
                sm: "setu:size-3",
                lg: "setu:size-5",
            },
        },
        defaultVariants: {
            size: "default",
        },
    }
);

/**
 * Radio Group Component
 *
 * A set of checkable buttons—known as radio buttons—where no more than one of the buttons can be checked at a time.
 * Built on Radix UI Radio Group primitive following shadcn/ui design standards.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <RadioGroup defaultValue="option-one">
 *   <div className="flex items-center space-x-2">
 *     <RadioGroupItem value="option-one" id="r1" />
 *     <Label htmlFor="r1">Option One</Label>
 *   </div>
 *   <div className="flex items-center space-x-2">
 *     <RadioGroupItem value="option-two" id="r2" />
 *     <Label htmlFor="r2">Option Two</Label>
 *   </div>
 * </RadioGroup>
 *
 * // Horizontal layout
 * <RadioGroup orientation="horizontal" defaultValue="option-one">
 *   <RadioGroupItem value="option-one" id="r1" />
 *   <RadioGroupItem value="option-two" id="r2" />
 * </RadioGroup>
 *
 * // Different sizes
 * <RadioGroupItem size="sm" value="small" />
 * <RadioGroupItem size="lg" value="large" />
 * ```
 */
export interface RadioGroupProps
    extends Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>, "orientation">,
        VariantProps<typeof radioGroupVariants> {}

const RadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, RadioGroupProps>(
    ({ className, orientation, ...props }, ref) => {
        return (
            <RadioGroupPrimitive.Root
                className={cn(radioGroupVariants({ orientation }), className)}
                {...props}
                ref={ref}
            />
        );
    }
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

/**
 * Radio Group Item Component
 *
 * Individual radio button within a RadioGroup.
 *
 * @example
 * ```tsx
 * <RadioGroupItem value="option" id="radio1" />
 * ```
 */
export interface RadioGroupItemProps
    extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
        VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(
    ({ className, size, ...props }, ref) => {
        return (
            <RadioGroupPrimitive.Item ref={ref} className={cn(radioGroupItemVariants({ size }), className)} {...props}>
                <RadioGroupPrimitive.Indicator className="setu:relative setu:flex setu:items-center setu:justify-center">
                    <Circle className="setu:fill-primary setu:absolute setu:top-1/2 setu:left-1/2 setu:size-2 setu:-translate-x-1/2 setu:-translate-y-1/2" />
                </RadioGroupPrimitive.Indicator>
            </RadioGroupPrimitive.Item>
        );
    }
);
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
